/**
 * Pure time-tracking rules. Everything is a function of (endAt, now, settings) so the
 * API, the background jobs and the tests all agree.
 */
export type LiveStatus = "active" | "expiring" | "overdue";

const MIN = 60_000;

/** `overdue` as soon as the paid time is over (even inside the grace window). */
export function deriveStatus(endAt: Date, now: Date, warnMinutes: number): LiveStatus {
  const msLeft = endAt.getTime() - now.getTime();
  if (msLeft < 0) return "overdue";
  if (msLeft <= warnMinutes * MIN) return "expiring";
  return "active";
}

/** Seconds until endAt (negative when overdue). */
export function remainingSeconds(endAt: Date, now: Date): number {
  return Math.round((endAt.getTime() - now.getTime()) / 1000);
}

/** Minutes past the end of the paid time, after the free grace window. 0 inside grace. */
export function overtimeMinutes(endAt: Date, now: Date, graceMinutes: number): number {
  const overMs = now.getTime() - endAt.getTime();
  if (overMs <= 0) return 0;
  return Math.max(0, Math.ceil(overMs / MIN) - graceMinutes);
}

/** Fine in fils: charged per started 15 minutes of overtime. */
export function overtimeFee(
  endAt: Date,
  now: Date,
  graceMinutes: number,
  feeFilsPer15Minutes: number,
): number {
  const minutes = overtimeMinutes(endAt, now, graceMinutes);
  return Math.ceil(minutes / 15) * feeFilsPer15Minutes;
}

/** Percentage (0–100) of the purchased time already used, for progress bars. */
export function progressPercent(startAt: Date, endAt: Date, now: Date): number {
  const total = endAt.getTime() - startAt.getTime();
  if (total <= 0) return 100;
  const used = now.getTime() - startAt.getTime();
  return Math.min(100, Math.max(0, Math.round((used / total) * 100)));
}
